import { REACT_APP_CLOUDINARY_CLOUD_NAME } from 'env'
import { Integration, Unsplash, UploadsLibrary } from 'proseShared/IntegrationsIcons'
import { useState } from 'react'
import { SelectedImage } from 'types'
import { reportError } from 'utils/error'

interface Props {
  // this is empty when user clicks "Cancel" within the prompt
  onSubmit: (payload?: SelectedImage[], saveInUploadsFolder?: boolean) => void
  initialIntegration?: Integration
  uploadHighRes?: boolean
}

export const useMediaUploadPrompt = ({ onSubmit, initialIntegration, uploadHighRes = false }: Props) => {
  const [stagedImages, setStagedImages] = useState<SelectedImage[]>([])
  const [selectedImages, setSelectedImages] = useState<SelectedImage[]>([])
  const [activeIntegration, setActiveIntegration] = useState<Integration | undefined>(initialIntegration)
  const [loading, setLoading] = useState(false)
  const [defaultUploadContainer, setDefaultUploadContainer] = useState(true)

  const uploadImages = async () => {
    setLoading(true)

    const formData = new FormData()
    const payload: SelectedImage[] = []

    try {
      for (let selectedImage of selectedImages) {
        // cloudinary accepts either a blob or url
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const fileUrl = uploadHighRes ? selectedImage.full_resolution_url! : selectedImage.url
        const fileToUpload = selectedImage.file ? selectedImage.file : fileUrl
        formData.append('file', fileToUpload)
        formData.append('upload_preset', 'workspace_portal_uploads')

        const response = await fetch(
          `https://api.cloudinary.com/v1_1/${REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`,
          {
            method: 'POST',
            body: formData,
          },
        )
        const parsedResponse = await response.json()
        if (activeIntegration === Unsplash) {
          parsedResponse.unsplash = selectedImage.unsplash
        }
        payload.push({ ...parsedResponse, url: parsedResponse.secure_url || parsedResponse.url })
      }
    } catch (error) {
      reportError(error, { severity: 'error' })
    }

    setLoading(false)

    return payload
  }

  const resetLocalState = () => {
    setStagedImages([])
    setSelectedImages([])
    setDefaultUploadContainer(true)
  }

  const onCancel = () => {
    onSubmit()
    resetLocalState()
  }

  const onSuccess = async () => {
    if (activeIntegration === UploadsLibrary) {
      onSubmit(selectedImages)
    } else {
      const payload = await uploadImages()
      onSubmit(payload, true)
    }
    resetLocalState()
  }

  const onActiveIntegrationChange = (integration: Integration) => {
    setActiveIntegration(integration)
    setStagedImages([])
    setSelectedImages([])
  }

  return {
    onActiveIntegrationChange,
    onSuccess,
    onCancel,
    resetLocalState,
    uploadImages,
    defaultUploadContainer,
    setDefaultUploadContainer,
    loading,
    setLoading,
    stagedImages,
    setStagedImages,
    selectedImages,
    setSelectedImages,
    activeIntegration,
    setActiveIntegration,
  }
}
